<script setup lang="ts">
import { ref, watch, nextTick, reactive, onMounted, onBeforeUnmount } from 'vue';
import { route } from '@hybridly/core';
import { router } from 'hybridly/vue';
import { marked } from 'marked';
import { usePageMeta } from '@/hooks/usePageMeta';
import IconArrowUp from '@/icons/line/arrow-up.svg';
import IconThumbsUp from '@/icons/line/thumbs-up.svg';
import IconThumbsDown from '@/icons/line/thumbs-down.svg';
import { indexChats, storeChat, type Chat as ApiChat } from '@/api/ChatApi';
import { rateChatMessage, streamChatMessage } from '@/api/ChatMessageApi';
import RichContent from '@/components/richTextInput/RichContent.vue';

// Initialize page metadata (title, breadcrumbs, etc.)
usePageMeta();

// Local interfaces extend/complement API interfaces where needed
interface ContentBlock {
  content: { text: string; html?: string };
  type: string;
}

interface Message {
  id?: number;
  // "assistant" | "user" – keeping it simple as requested
  type: string;
  content_blocks?: ContentBlock[];
  contentBlocks?: ContentBlock[]; // backend eager loads camel or snake depending
  content?: string; // for assistant replies returned by API
  rating?: number; // -1 thumbs down, 0 unrated, 1 thumbs up
}

// The current chat may include a fully-loaded messages array with the richer
// Message interface above, whereas the sidebar list only needs the base API
// chat structure (id & title).
type ChatWithMessages = Omit<ApiChat, 'messages'> & {
  messages?: Message[];
};

// Markdown renderer
marked.setOptions({
  async: false,
  breaks: true,
  gfm: true
});

const props = defineProps<{ chat: ChatWithMessages; chats: ApiChat[] }>();

const messages = ref<Message[]>([...(props.chat.messages ?? [])]);
const userInput = ref('');
const messagesEnd = ref<HTMLElement | null>(null);

// Sidebar list only needs minimal data, so we stick with the API type.
const sidebarChats = ref<ApiChat[]>([...(props.chats ?? [])]);

function scrollToBottom() {
  nextTick(() => {
    messagesEnd.value?.scrollIntoView({ behavior: 'smooth' });
  });
}

function getMessageContent(message: Message): string | null {
  const htmlContent =
    message.content_blocks?.[0]?.content?.html || message.contentBlocks?.[0]?.content?.html;

  if (htmlContent) {
    return htmlContent;
  }

  const textContent =
    message.content ||
    message.content_blocks?.[0]?.content?.text ||
    message.contentBlocks?.[0]?.content?.text;

  if (textContent) {
    return marked(textContent) as string;
  }

  return null;
}

watch(messages, scrollToBottom, { deep: true });

async function send() {
  const content = userInput.value.trim();
  if (!content) {
    alert('Please enter a message');
    return;
  }

  // Push the user's message immediately
  messages.value.push({ type: 'user', content });
  userInput.value = '';

  // Placeholder assistant message shown while waiting for the stream – keep `content` empty so the UI can detect the loading state.
  const assistantMessage = reactive<Message>({ type: 'assistant', content: '' });
  messages.value.push(assistantMessage);

  try {
    streamChatMessage(props.chat.id, content, 'chatbot-playground').then(async (response) => {
      const stream = response.data;

      // consume response
      const reader = stream.pipeThrough(new TextDecoderStream()).getReader();
      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        // value is decoded text chunk
        const lines = value.split('\n');
        for (const line of lines) {
          if (!line.startsWith('data: ')) {
            continue;
          }

          // Do NOT trim here; leading spaces are meaningful (tokens often start with a space)
          const data = line.slice(6);

          if (data === '[DONE]') {
            reader.cancel();
            return;
          }

          assistantMessage.content += JSON.parse(data);
        }
      }
    });
  } catch (e) {
    console.error(e);
    assistantMessage.content = 'Error communicating with server.';
  }
}

function openChat(chatId: number) {
  if (chatId === props.chat.id) {
    return;
  }

  router.get(route('chat.chatbot-playground', { chat: chatId }));
}

async function newChat() {
  try {
    const { chat } = await storeChat();
    router.get(route('chat.chatbot-playground', { chat: chat.id }));
  } catch (error) {
    console.error('Error starting new chat', error);
  }
}

async function fetchChats() {
  try {
    const { chats } = await indexChats();
    sidebarChats.value = chats;
  } catch (error) {
    console.error('Error fetching chats', error);
  }
}

async function rateMessage(message: Message, rating: number) {
  // Prevent rating messages that aren't persisted yet
  if (!message.id) {
    return;
  }

  try {
    const { message: updatedMessage } = await rateChatMessage(message.id, rating);
    // Update the local message with the new rating
    message.rating = updatedMessage.rating;
  } catch (error) {
    console.error('Error rating message', error);
  }
}

let pollingInterval: number | undefined;

onMounted(() => {
  // Initial fetch to sync immediately
  fetchChats();
  // Start polling every 5 seconds
  pollingInterval = window.setInterval(fetchChats, 5000);
});

onBeforeUnmount(() => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
  }
});
</script>

<template>
  <div class="flex h-full">
    <!-- Sidebar -->
    <aside class="w-64 space-y-2 overflow-y-auto border-r p-4">
      <button
        @click="newChat"
        class="mb-2 w-full rounded-lg bg-green-600 px-3 py-2 text-center text-white hover:bg-green-700"
      >
        + New Chat
      </button>
      <div
        v-for="c in sidebarChats"
        :key="c.id"
        @click="openChat(c.id)"
        class="cursor-pointer rounded-lg px-3 py-2 hover:bg-slate-100"
        :class="c.id === props.chat.id ? 'bg-blue-100 font-semibold' : ''"
      >
        {{ c.title || `Chat #${c.id}` }}
      </div>
    </aside>

    <!-- Chat Area -->
    <div class="flex h-full flex-1 flex-col">
      <div class="flex-1 space-y-4 overflow-y-auto p-4">
        <div
          v-for="(m, index) in messages"
          :key="index"
          :class="m.type === 'user' ? 'text-right' : 'text-left'"
        >
          <div
            class="inline-block rounded-lg px-4 py-2"
            :class="[
              m.type === 'user' ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-900',
              {
                'animate-pulse':
                  m.type !== 'user' &&
                  !(m.content || m.content_blocks?.length || m.contentBlocks?.length)
              }
            ]"
          >
            <RichContent
              v-if="getMessageContent(m)"
              :html="getMessageContent(m)!"
              :styleLinks="false"
            />
          </div>

          <!-- Rating buttons for assistant messages -->
          <div
            v-if="m.type === 'assistant' && m.id"
            class="mt-1 flex items-center justify-start gap-1 text-slate-500"
          >
            <button
              class="p-1 hover:text-blue-600"
              :class="{ 'text-blue-600': m.rating === 1 }"
              @click="rateMessage(m, 1)"
            >
              <IconThumbsUp class="h-5 w-5" />
            </button>
            <button
              class="p-1 hover:text-blue-600"
              :class="{ 'text-blue-600': m.rating === -1 }"
              @click="rateMessage(m, -1)"
            >
              <IconThumbsDown class="h-5 w-5" />
            </button>
          </div>
        </div>
        <div ref="messagesEnd"></div>
      </div>

      <form @submit.prevent="send" class="flex items-center justify-center gap-2 border-t p-4">
        <textarea
          v-model="userInput"
          @keydown.enter.exact.prevent="send"
          class="flex-1 resize-none rounded-lg border p-2"
          rows="2"
          placeholder="Type your message..."
          autofocus
        />
        <button
          type="submit"
          class="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700"
        >
          <IconArrowUp class="h-8 w-8 text-white" />
        </button>
      </form>
    </div>
  </div>
</template>

<style scoped>
textarea::-webkit-scrollbar {
  display: none;
}
</style>
